import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FaClipboardList,
  FaArrowLeft,
  FaKeyboard,
  FaGlobe,
  FaTimes,
  FaGripHorizontal,
} from "react-icons/fa";
import moment from "moment";
import dayjs from "dayjs";
import styles from "../AdmissionApplicationForm.module.css";
import {
  CustomFileInput,
  CustomRadioGroup,
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
} from "../../../components";
import {
  fetchOccupationOptions,
  fetchCountryOptions,
  fetchStateOptions,
  fetchReligionOptions,
  fetchCategoryOptions,
  fetchClassOptions,
  GENDER_OPTIONS,
  STATE_TYPES,
} from "../../../redux/Masterdata";
import { switchLanguage } from "../../../redux/languageSlice";
import { validateAdmissionForm } from "./Validationx";
import AxiosInstance, {
  admissionLogo1,
  admissionLogo2,
} from "../../../services/Api";
import PopupModal from "../../../components/PopupModal/PopupModal";
import ModalX from "./Modalx";

// ==================== DATE TO WORDS CONVERTER ====================
const numberToWords = (num) => {
  const ones = [
    "",
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
    "Eleventh",
    "Twelfth",
    "Thirteenth",
    "Fourteenth",
    "Fifteenth",
    "Sixteenth",
    "Seventeenth",
    "Eighteenth",
    "Nineteenth",
  ];
  const tens = [
    "",
    "",
    "Twentieth",
    "Thirtieth",
    "Fortieth",
    "Fiftieth",
    "Sixtieth",
    "Seventieth",
    "Eightieth",
    "Ninetieth",
  ];
  const tensPrefix = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  if (num <= 0) return "";
  if (num < 20) return ones[num];
  if (num % 10 === 0) return tens[Math.floor(num / 10)];
  return tensPrefix[Math.floor(num / 10)] + "-" + ones[num % 10].toLowerCase();
};

const yearToWords = (year) => {
  const onesW = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tensW = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const convertTwoDigit = (n) => {
    if (n === 0) return "";
    if (n < 20) return onesW[n];
    return (
      tensW[Math.floor(n / 10)] +
      (n % 10 ? "-" + onesW[n % 10].toLowerCase() : "")
    );
  };
  if (year >= 2000) {
    const remainder = year - 2000;
    if (remainder === 0) return "Two Thousand";
    if (remainder < 10) return "Two Thousand and " + onesW[remainder];
    return "Two Thousand and " + convertTwoDigit(remainder);
  }
  const first = Math.floor(year / 100);
  const last = year % 100;
  return (
    convertTwoDigit(first) +
    " Hundred" +
    (last > 0 ? " and " + convertTwoDigit(last) : "")
  );
};

const convertDateToWords = (dateStr) => {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  if (!d.isValid()) return "";
  const day = d.date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[d.month()];
  const year = d.year();
  return `${numberToWords(day)} ${month} ${yearToWords(year)}`;
};

// ==================== STATIC OPTIONS ====================
const LIVING_WITH_OPTIONS = [
  { value: "", label: "Select" },
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Guardian" },
];

const MEDIUM_OPTIONS = [
  { value: "TM", label: "TM", disabled: false },
  { value: "EM", label: "EM", disabled: false },
];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes", disabled: false },
  { value: "No", label: "No", disabled: false },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "TC", label: "TC", disabled: false },
  { value: "Record Sheet", label: "Record Sheet", disabled: false },
];

const MOTHER_TONGUE_OPTIONS = [
  { value: "", label: "Select Mother Tongue" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "Others", label: "Others" },
];

const LANGUAGE_OPTIONS = [
  { value: "", label: "Select" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "French", label: "French" },
  { value: "Sanskrit", label: "Sanskrit" },
];

// ==================== TAMIL KEYBOARD LAYOUT ====================
const TAMIL_VOWELS = [
  "அ",
  "ஆ",
  "இ",
  "ஈ",
  "உ",
  "ஊ",
  "எ",
  "ஏ",
  "ஐ",
  "ஒ",
  "ஓ",
  "ஔ",
];
const TAMIL_CONSONANTS = [
  "க",
  "ங",
  "ச",
  "ஞ",
  "ட",
  "ண",
  "த",
  "ந",
  "ப",
  "ம",
  "ய",
  "ர",
  "ல",
  "வ",
  "ழ",
  "ள",
  "ற",
  "ன",
  "ஜ",
  "ஷ",
  "ஸ",
  "ஹ",
];
const TAMIL_MODIFIERS = [
  "்",
  "ா",
  "ி",
  "ீ",
  "ு",
  "ூ",
  "ெ",
  "ே",
  "ை",
  "ொ",
  "ோ",
  "ௌ",
];

// ==================== YEAR OPTIONS ====================
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [{ value: "", label: "Select Year" }];
  for (let y = currentYear; y >= currentYear - 20; y--) {
    years.push({ value: y.toString(), label: y.toString() });
  }
  return years;
};
const YEAR_OPTIONS = generateYearOptions();

// ==================== INITIAL FORM DATA ====================
const getInitialFormData = () => ({
  date: moment().format("YYYY-MM-DD"),
  student_name_in_tamil: "",
  student_name: "",
  date_of_birth: null,
  date_of_birth_words: "",
  aadhar_no: "",
  email: "",
  mobile_number: "",
  gender: "",
  religion: "",
  caste: "",
  community: "",
  nationality: "Indian",
  student_native_state: "",
  living_with: "",
  parent_name: "",
  parent_occupation: "",
  parent_annual_salary: "",
  parent_address: "",
  parent_city: "",
  parent_district: "",
  parent_state: "",
  parent_pincode: "",
  guardian_name: "",
  guardian_occupation: "",
  guardian_annual_salary: "",
  guardian_address: "",
  guardian_city: "",
  guardian_district: "",
  guardian_state: "",
  guardian_pincode: "",
  last_studied_class: "",
  last_studied_school: "",
  emis_id: "",
  last_studied_medium: "TM",
  qualified_higher_secondary: "No",
  attached_document_type: "TC",
  document_path: "",
  document_original_name: "",
  preferred_class: "",
  mother_tongue: "",
  preferred_first_language: "",
  preferred_stream: "TM",
  is_vaccinated_pox: "Yes",
  previous_school_address_1: "",
  previous_studied_class_1: "",
  previous_studied_medium_1: "TM",
  previous_studied_year_1: "",
  previous_school_address_2: "",
  previous_studied_class_2: "",
  previous_studied_medium_2: "TM",
  previous_studied_year_2: "",
  previous_school_address_3: "",
  previous_studied_class_3: "",
  previous_studied_medium_3: "TM",
  previous_studied_year_3: "",
  identification_mark_1: "",
  identification_mark_2: "",
  place: "",
  student_image: "",
  student_image_original_name: "",
});

// ==================== NUMBER-ONLY FIELDS ====================
const NUMBER_ONLY_FIELDS = [
  "mobile_number",
  "aadhar_no",
  "parent_pincode",
  "guardian_pincode",
  "emis_id",
  "parent_annual_salary",
  "guardian_annual_salary",
];

const SUPPORTED_URL_LANGS = ["en", "ta"];

// ==================== MAIN COMPONENT ====================
const AdmissionApplicationFormX = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { "*": splat } = useParams();
  const { translations, language } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  // Parse splat into viewId, urlLang, admissionId based on content
  const { viewId, urlLang, admissionId } = useMemo(() => {
    if (!splat) return { viewId: null, urlLang: null, admissionId: "" };

    const segments = splat.split("/").filter(Boolean);
    let viewId = null;
    let urlLang = null;

    const remaining = [];
    segments.forEach((seg) => {
      if (/^\d+$/.test(seg) && viewId === null) {
        viewId = seg;
      } else if (SUPPORTED_URL_LANGS.includes(seg) && urlLang === null) {
        urlLang = seg;
      } else {
        remaining.push(seg);
      }
    });

    const admissionId = remaining.join("/");

    return { viewId, urlLang, admissionId };
  }, [splat]);

  // 🔹 If URL has a language segment, switch app language to it
  useEffect(() => {
    if (
      urlLang &&
      SUPPORTED_URL_LANGS.includes(urlLang) &&
      urlLang !== language
    ) {
      dispatch(switchLanguage(urlLang));
      localStorage.setItem("appLanguage", urlLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLang]);

  // ==================== REDUX STATE ====================
  const {
    occupationOptions: reduxOccupationOptions,
    religionOptions: reduxReligionOptions,
    categoryOptions: reduxCategoryOptions,
    countryOptions: reduxCountryOptions,
    fatherStateOptions: reduxFatherStateOptions,
    classOptions: reduxClassOptions,
  } = useSelector((state) => state.masterData);

  // ==================== LOCAL STATE ====================
  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [admissionConfig, setAdmissionConfig] = useState(null);

  // School logo — fetched live through the public blob-aware endpoint
  // (plain <img src> cannot send the Tenant-ID header, so fetch as a
  // blob via AxiosInstance and show an object URL). Falls back to the
  // bundled default logos until it loads / on failure.
  const [schoolLogoUrl, setSchoolLogoUrl] = useState(null);
  useEffect(() => {
    const fetchSchoolLogo = async () => {
      try {
        const res = await AxiosInstance.get("/admission/school-logo-image", {
          params: { school_id: admissionConfig.school_id },
          responseType: "blob",
        });
        setSchoolLogoUrl(URL.createObjectURL(res.data));
      } catch (err) {
        // keep the default bundled logo
      }
    };
    if (admissionConfig?.school_id) fetchSchoolLogo();
  }, [admissionConfig?.school_id]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [admissionStatus, setAdmissionStatus] = useState(null);

  // ---- Tamil Keyboard State ----
  const [showTamilKeyboard, setShowTamilKeyboard] = useState(false);
  const [activeInputName, setActiveInputName] = useState("");

  // ---- Draggable Keyboard State ----
  const [kbPosition, setKbPosition] = useState({ x: 100, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);
  const otpInputRef = useRef(null);

  // ---- Language Dropdown Options ----
  const appLanguageOptions = [
    { code: "en", label: "English" },
    { code: "ta", label: "தமிழ்" },
  ];

  const handleLanguageChange = (e) => {
    const langCode = e.target.value;
    dispatch(switchLanguage(langCode));
    localStorage.setItem("appLanguage", langCode);
  };

  // ⭐ Edit mode detection
  const isEditMode = useMemo(() => Boolean(viewId), [viewId]);

  // ==================== DRAGGABLE KEYBOARD HANDLERS ====================
  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragOffset.current = {
        x: clientX - kbPosition.x,
        y: clientY - kbPosition.y,
      };
      setIsDragging(true);
    },
    [kbPosition],
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const newX = clientX - dragOffset.current.x;
      const newY = clientY - dragOffset.current.y;
      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;
      setKbPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ==================== MEMOIZED DROPDOWN OPTIONS ====================
  const genderOptions = useMemo(
    () =>
      GENDER_OPTIONS.map((opt) => ({
        ...opt,
        label: t?.[opt.label?.toLowerCase()] || opt.label,
      })),
    [t],
  );

  const religionOptions = useMemo(
    () => [
      { value: "", label: t?.selectReligion || "Select Religion" },
      ...reduxReligionOptions,
    ],
    [reduxReligionOptions, t],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "", label: t?.selectCategory || "Select Category" },
      ...reduxCategoryOptions,
    ],
    [reduxCategoryOptions, t],
  );

  const stateOptions = useMemo(
    () => [
      { value: "", label: t?.selectState || "Select State" },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions, t],
  );

  const occupationOptions = useMemo(
    () => [
      { value: "", label: t?.selectOccupation || "Select Occupation" },
      ...reduxOccupationOptions,
    ],
    [reduxOccupationOptions, t],
  );

  const classOptions = useMemo(() => {
    const filteredClasses = reduxClassOptions.filter(
      (item) => item.label !== "XI" && item.label !== "XII",
    );
    return [
      { value: "", label: t?.selectClass || "Select Class" },
      ...filteredClasses,
    ];
  }, [reduxClassOptions, t]);

  const livingWithOptions = useMemo(
    () =>
      LIVING_WITH_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.select || "Select"
            : opt.value === "parent"
              ? t?.parent || "Parent"
              : t?.guardian || "Guardian",
      })),
    [t],
  );

  const motherTongueOptions = useMemo(
    () =>
      MOTHER_TONGUE_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.selectMotherTongue || "Select Mother Tongue"
            : opt.label,
      })),
    [t],
  );

  const languageOptions = useMemo(
    () =>
      LANGUAGE_OPTIONS.map((opt) => ({
        ...opt,
        label: opt.value === "" ? t?.select || "Select" : opt.label,
      })),
    [t],
  );

  // ==================== INITIAL DATA FETCH ====================
  useEffect(() => {
    fetchAdmissionCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAdmissionCheck = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post("/admission/admissionCheck", {
        id: admissionId,
        admission_type: "VI-X",
      });
      if (response.data?.status) {
        setAdmissionConfig(response.data.data);
        fetchAllMasterData(response.data.data);
        if (viewId) {
          // EDIT MODE: fetch existing admission
          fetchAdmissionView();
        }
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            response.data.message ||
            "Failed to fetch details. Please try again.",
        });
      }
    } catch (error) {
      console.error("Admission check error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing admission data for editing
  const fetchAdmissionView = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/admission/viewSecondryAdmission",
        { id: viewId },
      );
      if (response.data?.status) {
        const data = response.data.data;
        setAdmissionStatus(data.status);
        const formLang = data?.form_language || "en";
        dispatch(switchLanguage(formLang));
        setAdmissionConfig({
          school_id: data.school_id,
          academic_year_id: data.academic_years,
          school_name: data.school_name || "",
        });

        fetchAllMasterData({
          school_id: data.school_id,
          academic_year_id: data.academic_year,
        });

        // ⭐ If status is Withdraw → show popup and block editing
        if (data.status === "Withdraw") {
          setModalState({
            open: true,
            type: "failed",
            message:
              t?.statusWithdrawMessage ||
              "Status is Withdraw. You cannot edit this application.",
            redirectPath: "/",
          });
          return;
        }

        populateFormData(data);
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            response.data.message ||
            "Failed to fetch admission details. Please try again.",
        });
      }
    } catch (error) {
      console.error("Admission view error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Map API response to formData
  const populateFormData = (data) => {
    setFormData({
      date: data.date
        ? dayjs(data.date).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      student_name_in_tamil: data.student_name_in_tamil || "",
      student_name: data.student_name || "",
      date_of_birth: data.date_of_birth
        ? dayjs(data.date_of_birth).format("YYYY-MM-DD")
        : null,
      date_of_birth_words: data.date_of_birth_words || "",
      aadhar_no: data.aadhaar_no || "",
      email: data.email || "",
      mobile_number: data.mobile_number || "",
      gender: data.gender || "",
      religion: data.religion || "",
      caste: data.caste || "",
      community: data.community || "",
      nationality: data.nationality || "Indian",
      student_native_state: data.student_native_state || "",
      living_with: data.living_with || "",
      parent_name: data.parent_name || "",
      parent_occupation: data.parent_occupation || "",
      parent_annual_salary: data.parent_annual_salary
        ? String(data.parent_annual_salary)
        : "",
      parent_address: data.parent_address || "",
      parent_city: data.parent_city || "",
      parent_district: data.parent_district || "",
      parent_state: data.parent_state || "",
      parent_pincode: data.parent_pincode ? String(data.parent_pincode) : "",
      guardian_name: data.guardian_name || "",
      guardian_occupation: data.guardian_occupation || "",
      guardian_annual_salary: data.guardian_annual_salary
        ? String(data.guardian_annual_salary)
        : "",
      guardian_address: data.guardian_address || "",
      guardian_city: data.guardian_city || "",
      guardian_district: data.guardian_district || "",
      guardian_state: data.guardian_state || "",
      guardian_pincode: data.guardian_pincode
        ? String(data.guardian_pincode)
        : "",
      last_studied_class: data.last_studied_class || "",
      last_studied_school: data.last_studied_school || "",
      emis_id: data.emis_id || "",
      last_studied_medium: data.last_studied_medium || "TM",
      qualified_higher_secondary: data.qualified_higher_secondary || "No",
      attached_document_type: data.attached_document_type || "TC",
      document_path: data.document_path || "",
      document_original_name: data.document_original_path || "",
      preferred_class: data.preferred_class || "",
      mother_tongue: data.mother_tongue || "",
      preferred_first_language: data.preferred_first_language || "",
      preferred_stream: data.preferred_stream || "TM",
      is_vaccinated_pox: data.is_vaccinated_pox || "Yes",
      previous_school_address_1: data.previous_school_address_1 || "",
      previous_studied_class_1: data.previous_studied_class_1 || "",
      previous_studied_medium_1: data.previous_studied_medium_1 || "TM",
      previous_studied_year_1: data.previous_studied_year_1
        ? String(data.previous_studied_year_1)
        : "",
      previous_school_address_2: data.previous_school_address_2 || "",
      previous_studied_class_2: data.previous_studied_class_2 || "",
      previous_studied_medium_2: data.previous_studied_medium_2 || "TM",
      previous_studied_year_2: data.previous_studied_year_2
        ? String(data.previous_studied_year_2)
        : "",
      previous_school_address_3: data.previous_school_address_3 || "",
      previous_studied_class_3: data.previous_studied_class_3 || "",
      previous_studied_medium_3: data.previous_studied_medium_3 || "TM",
      previous_studied_year_3: data.previous_studied_year_3
        ? String(data.previous_studied_year_3)
        : "",
      identification_mark_1: data.identification_mark_1 || "",
      identification_mark_2: data.identification_mark_2 || "",
      place: data.place || "",
      student_image: data.student_image || "",
      student_image_original_name: data.student_original_path || "",
    });
  };

  const fetchAllMasterData = (data) => {
    dispatch(fetchReligionOptions());
    dispatch(fetchCategoryOptions());
    dispatch(fetchOccupationOptions());
    dispatch(
      fetchClassOptions({
        id: data.school_id,
        academic_year: data.academic_year_id,
      }),
    );
    dispatch(fetchCountryOptions()).then((action) => {
      if (action.payload && Array.isArray(action.payload)) {
        const india = action.payload.find(
          (c) => c.label?.toLowerCase() === "india",
        );
        if (india) {
          dispatch(
            fetchStateOptions({
              countryId: india.value,
              stateType: STATE_TYPES.FATHER,
            }),
          );
        }
      }
    });
  };

  // ==================== HANDLERS ====================
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (NUMBER_ONLY_FIELDS.includes(name)) {
        if (value !== "" && !/^\d*$/.test(value)) return;
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleRadioChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDateChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
        ...(name === "date_of_birth"
          ? { date_of_birth_words: convertDateToWords(formatted) }
          : {}),
      }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
      if (name === "date_of_birth" && errors.date_of_birth_words) {
        setErrors((prev) => ({ ...prev, date_of_birth_words: "" }));
      }
    },
    [errors],
  );

  // ---- Tamil Keyboard Handlers ----
  const handleTamilInputFocus = (name) => {
    if (!NUMBER_ONLY_FIELDS.includes(name)) setActiveInputName(name);
  };

  const handleTamilKeyPress = (char) => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + char,
    }));
    if (errors[activeInputName])
      setErrors((prev) => ({ ...prev, [activeInputName]: "" }));
  };

  const handleTamilBackspace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "").slice(0, -1),
    }));
  };

  const handleTamilSpace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + " ",
    }));
  };

  // ---- File upload handlers ----
  const handleDocumentChange = useCallback(
    async (file) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          document_original_name: "",
          document_path: "",
        }));
        setErrors((prev) => ({ ...prev, document_path: "" }));
        return;
      }
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          document_path: t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG",
        }));
        return;
      }
      try {
        const payload = new FormData();
        payload.append("transfer_certificate", file);
        if (admissionConfig?.school_id) {
          payload.append("school_id", admissionConfig.school_id);
        }
        const response = await AxiosInstance.post(
          "/groupCockpit/upload",
          payload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploaded = response.data[0];
          setFormData((prev) => ({
            ...prev,
            document_original_name: uploaded.original_name,
            document_path: uploaded.upload_path,
          }));
          setErrors((prev) => ({ ...prev, document_path: "" }));
        }
      } catch (error) {
        console.error("Document upload error:", error);
        setErrors((prev) => ({
          ...prev,
          document_path:
            t?.fileUploadFailed || "Failed to upload file. Please try again.",
        }));
      }
    },
    [t, admissionConfig?.school_id],
  );

  const handleStudentImageChange = useCallback(
    async (file) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          student_image_original_name: "",
          student_image: "",
        }));
        setErrors((prev) => ({ ...prev, student_image: "" }));
        return;
      }
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          student_image:
            t?.onlyImageFilesAllowed || "Only jpg, jpeg, png files are allowed",
        }));
        return;
      }
      try {
        const payload = new FormData();
        payload.append("admission_student_image", file);
        if (admissionConfig?.school_id) {
          payload.append("school_id", admissionConfig.school_id);
        }
        const response = await AxiosInstance.post(
          "/groupCockpit/upload",
          payload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploaded = response.data[0];
          setFormData((prev) => ({
            ...prev,
            student_image_original_name: uploaded.original_name,
            student_image: uploaded.upload_path,
          }));
          setErrors((prev) => ({ ...prev, student_image: "" }));
        }
      } catch (error) {
        console.error("Student image upload error:", error);
        setErrors((prev) => ({
          ...prev,
          student_image:
            t?.fileUploadFailed || "Failed to upload file. Please try again.",
        }));
      }
    },
    [t, admissionConfig?.school_id],
  );

  // ==================== VERIFY APPLICATION ====================
  const handleVerifyApplication = async (e) => {
    e.preventDefault();
    const validationErrors = validateAdmissionForm(formData, translations);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      setLoading(true);
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const response = await AxiosInstance.post("/admission/sendOTP", {
        school_id: admissionId,
        email: formData.email,
        otp: otp,
      });
      if (response.data?.status) {
        setGeneratedOtp(otp);
        setModalState({
          open: true,
          type: "success",
          message: t?.otpSentSuccessfully || "OTP sent successfully.",
          redirectPath: "",
        });
        setErrors({});
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error("Admission check error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== CONFIRM & SUBMIT ====================
  const handleConfirmSubmit = async (otp) => {
    const getVal = (field) => {
      if (!field) return null;
      if (typeof field === "object" && field.value !== undefined)
        return field.value;
      return field;
    };
    const getLabel = (field) => {
      if (!field) return null;
      if (typeof field === "object" && field.label !== undefined)
        return field.label;
      return field;
    };

    const livingWith = getVal(formData.living_with);
    const isParent = livingWith === "parent";
    const isGuardian = livingWith === "guardian";

    try {
      setSubmitting(true);

      // ⭐ Build base payload (common fields for both create and update)
      const basePayload = {
        date: formData.date,
        student_name: formData.student_name,
        student_name_in_tamil: formData.student_name_in_tamil,
        emis_id: formData.emis_id || null,
        email: formData.email,
        mobile_number: formData.mobile_number,
        student_image: formData.student_image || null,
        student_original_path: formData.student_image_original_name || null,
        gender: getLabel(formData.gender),
        date_of_birth: formData.date_of_birth,
        date_of_birth_words: formData.date_of_birth_words,
        religion: getLabel(formData.religion),
        caste: formData.caste,
        community: getLabel(formData.community),
        nationality: formData.nationality,
        student_native_state: getLabel(formData.student_native_state),
        aadhaar_no: formData.aadhar_no || null,
        living_with: livingWith,
        parent_name: isParent ? formData.parent_name : null,
        parent_occupation: isParent
          ? getLabel(formData.parent_occupation)
          : null,
        parent_annual_salary: isParent
          ? parseInt(formData.parent_annual_salary) || 0
          : null,
        parent_address: isParent ? formData.parent_address : null,
        parent_city: isParent ? formData.parent_city : null,
        parent_district: isParent ? formData.parent_district : null,
        parent_state: isParent ? getLabel(formData.parent_state) : null,
        parent_pincode: isParent ? formData.parent_pincode : null,
        guardian_name: isGuardian ? formData.guardian_name : null,
        guardian_occupation: isGuardian
          ? getLabel(formData.guardian_occupation)
          : null,
        guardian_annual_salary: isGuardian
          ? parseInt(formData.guardian_annual_salary) || 0
          : null,
        guardian_address: isGuardian ? formData.guardian_address : null,
        guardian_city: isGuardian ? formData.guardian_city : null,
        guardian_district: isGuardian ? formData.guardian_district : null,
        guardian_state: isGuardian ? getLabel(formData.guardian_state) : null,
        guardian_pincode: isGuardian ? formData.guardian_pincode : null,
        last_studied_medium: formData.last_studied_medium,
        last_studied_class: getLabel(formData.last_studied_class),
        last_studied_school: formData.last_studied_school,
        qualified_higher_secondary: formData.qualified_higher_secondary,
        attached_document_type: formData.attached_document_type,
        document_path: formData.document_path || null,
        document_original_path: formData.document_original_name || null,
        preferred_class: getLabel(formData.preferred_class),
        mother_tongue: getVal(formData.mother_tongue),
        preferred_first_language: getVal(formData.preferred_first_language),
        preferred_stream: formData.preferred_stream,
        is_vaccinated_pox: formData.is_vaccinated_pox,
        previous_school_address_1: formData.previous_school_address_1 || null,
        previous_studied_class_1:
          getLabel(formData.previous_studied_class_1) || null,
        previous_studied_medium_1: formData.previous_studied_medium_1 || null,
        previous_studied_year_1:
          getVal(formData.previous_studied_year_1) || null,
        previous_school_address_2: formData.previous_school_address_2 || null,
        previous_studied_class_2:
          getLabel(formData.previous_studied_class_2) || null,
        previous_studied_medium_2: formData.previous_studied_medium_2 || null,
        previous_studied_year_2:
          getVal(formData.previous_studied_year_2) || null,
        previous_school_address_3: formData.previous_school_address_3 || null,
        previous_studied_class_3:
          getLabel(formData.previous_studied_class_3) || null,
        previous_studied_medium_3: formData.previous_studied_medium_3 || null,
        previous_studied_year_3:
          getVal(formData.previous_studied_year_3) || null,
        place: formData.place,
        identification_mark_1: formData.identification_mark_1,
        identification_mark_2: formData.identification_mark_2,
        form_language: language,
      };

      let response;

      if (isEditMode) {
        // ⭐ EDIT MODE: PUT to updateSecondryAdmission
        const updatePayload = {
          ...basePayload,
          id: viewId,
          status: admissionStatus || "Submitted",
          updated_by: "Parent",
        };

        response = await AxiosInstance.put(
          "/admission/updateSecondryAdmission",
          updatePayload,
        );
      } else {
        // ⭐ CREATE MODE: POST to createSecondryAdmission
        const createPayload = {
          ...basePayload,
          school_id: admissionId || "",
          created_by: "Parent",
        };

        response = await AxiosInstance.post(
          "/admission/createSecondryAdmission",
          createPayload,
        );
      }

      if (response.data?.status) {
        setShowPreviewModal(false);
        setGeneratedOtp("");
        setModalState({
          open: true,
          type: "success",
          message: isEditMode
            ? t?.updateSuccess || "Updated successfully!"
            : t?.createSuccess || "Created successfully!",
          // redirectPath: "/",
        });
        if (!isEditMode) {
          setFormData(getInitialFormData());
        }
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t?.saveFailed ||
            "Failed to save. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          t?.saveFailed ||
          "Failed to save. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    const { redirectPath } = modalState;
    setModalState({ open: false, type: "", message: "", redirectPath: "" });

    if (redirectPath && typeof redirectPath === "object" && redirectPath.path) {
      navigate(redirectPath.path, {
        state: redirectPath.state,
        replace: redirectPath.replace || false,
      });
    } else if (typeof redirectPath === "string" && redirectPath.trim() !== "") {
      navigate(redirectPath);
    }
  };

  const handleModalClose = () =>
    setModalState({ open: false, type: "", message: "" });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        {t?.loading || "Loading..."}
      </div>
    );
  }

  // ==================== TAMIL KEY STYLE ====================
  const tamilKeyStyle = {
    minWidth: "38px",
    height: "38px",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    transition: "background-color 0.15s",
    padding: "0 4px",
    userSelect: "none",
  };

  // ==================== RENDER ====================
  return (
    <>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <ModalX
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        formData={formData}
        onSubmit={handleConfirmSubmit}
        submitting={submitting}
        handleVerifyApplication={handleVerifyApplication}
        generatedOtp={generatedOtp}
        isEditMode={isEditMode}
        otpInputRef={otpInputRef}
      />

      {/* ==================== DRAGGABLE TAMIL KEYBOARD ==================== */}
      {showTamilKeyboard && (
        <div
          ref={keyboardRef}
          style={{
            position: "fixed",
            top: `${kbPosition.y}px`,
            left: `${kbPosition.x}px`,
            zIndex: 1050,
            backgroundColor: "#f8f8f8",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            padding: "0",
            width: "auto",
            maxWidth: "620px",
            minWidth: "380px",
            userSelect: "none",
          }}
        >
          <div
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
              background: "linear-gradient(135deg, #37474f 0%, #455a64 100%)",
              borderRadius: "12px 12px 0 0",
              cursor: isDragging ? "grabbing" : "grab",
              color: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FaGripHorizontal size={14} style={{ opacity: 0.6 }} />
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px" }}>
                {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
              </h5>
            </div>
            <button
              type="button"
              onClick={() => setShowTamilKeyboard(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
                color: "#fff",
                padding: "2px 4px",
                lineHeight: 1,
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div style={{ padding: "12px 16px 16px" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                marginBottom: "8px",
              }}
            >
              {TAMIL_VOWELS.map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                marginBottom: "8px",
              }}
            >
              {TAMIL_CONSONANTS.map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
              {TAMIL_MODIFIERS.map((char, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
              <button
                type="button"
                onClick={handleTamilBackspace}
                style={{
                  ...tamilKeyStyle,
                  minWidth: "44px",
                  backgroundColor: "#ffebee",
                  color: "#d32f2f",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                ⌫
              </button>
              <button
                type="button"
                onClick={handleTamilSpace}
                style={{
                  ...tamilKeyStyle,
                  minWidth: "60px",
                  backgroundColor: "#f5f5f5",
                  fontSize: "11px",
                  color: "#555",
                }}
              >
                ␣
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SCHOOL HEADER BANNER ==================== */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #1e88e5 100%)",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#fff",
          marginBottom: "0",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <img
            src={schoolLogoUrl || admissionLogo1}
            alt="School Logo"
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid rgba(255,255,255,0.5)",
              backgroundColor: "#fff",
            }}
          />
        </div>
        <div style={{ textAlign: "center", flex: 1, padding: "0 16px" }}>
          <h2
            style={{
              margin: "0 0 4px 0",
              fontSize: "22px",
              fontWeight: 700,
              letterSpacing: "0.5px",
            }}
          >
            {admissionConfig?.school_name || t?.schoolName || "School Name"}
          </h2>
          <p
            style={{
              margin: "0 0 2px 0",
              fontSize: "14px",
              fontWeight: 500,
              opacity: 0.9,
              color: "#fff",
            }}
          >
            {t?.formItoX || "Form - I to X"}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: 500,
              opacity: 0.9,
              color: "#fff",
            }}
          >
            {t?.academicYear || "Academic Year"}:{" "}
            <strong>{admissionConfig?.academic_year_id || ""}</strong>
          </p>
        </div>
        <div style={{ flexShrink: 0 }}>
          <img
            src={schoolLogoUrl || admissionLogo2}
            alt="School Logo"
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid rgba(255,255,255,0.5)",
              backgroundColor: "#fff",
            }}
          />
        </div>
      </div>

      <div className={styles.container}>
        {/* ==================== TOP TOOLBAR ==================== */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 0",
            marginBottom: "10px",
            borderBottom: "1px solid #e0e0e0",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: "#1565c0",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <FaArrowLeft size={14} />
              {t?.back || "Back"}
            </button>
            <button
              type="button"
              onClick={() => setShowTamilKeyboard((prev) => !prev)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: showTamilKeyboard ? "#2e7d32" : "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <FaKeyboard size={14} />
              {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaGlobe size={16} style={{ color: "#555" }} />
            <select
              value={language}
              onChange={handleLanguageChange}
              style={{
                padding: "8px 32px 8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: "#fff",
                cursor: "pointer",
                appearance: "auto",
              }}
            >
              {appLanguageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ==================== FORM ==================== */}
        <form onSubmit={handleVerifyApplication}>
          {/* SECTION: BASIC DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.basicDetails || "Basic Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.date || "Date"}
                name="date"
                type="text"
                value={formData.date}
                disabled
                readOnly
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.studentNameTamil || "Student's Name (Tamil)"}
                name="student_name_in_tamil"
                type="text"
                value={formData.student_name_in_tamil}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("student_name_in_tamil")}
                placeholder={
                  t?.studentNameTamilPlaceholder ||
                  "Enter Student Name in Tamil"
                }
                required
                error={errors.student_name_in_tamil}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.studentNameEnglish || "Student's Name (English)"}
                name="student_name"
                type="text"
                value={formData.student_name}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("student_name")}
                placeholder={
                  t?.studentNameEnglishPlaceholder ||
                  "Enter Student Name in English"
                }
                required
                error={errors.student_name}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={t?.dateOfBirth || "Date Of Birth"}
                name="date_of_birth"
                value={
                  formData.date_of_birth ? dayjs(formData.date_of_birth) : null
                }
                onChange={(date) => handleDateChange("date_of_birth", date)}
                placeholder={t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"}
                required
                error={errors.date_of_birth}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.dobInWords || "DOB in Words"}
                name="date_of_birth_words"
                type="text"
                value={formData.date_of_birth_words}
                disabled
                readOnly
                placeholder={
                  t?.dobInWordsPlaceholder || "Auto-filled from Date of Birth"
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.aadharNumber || "Aadhar No."}
                name="aadhar_no"
                type="text"
                value={formData.aadhar_no}
                onChange={handleInputChange}
                placeholder={
                  t?.aadharNumberAltPlaceholder || "Ex: 432156789076"
                }
                maxLength={12}
                error={errors.aadhar_no}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.emailAddress || "Email"}
                name="email"
                type="text"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t?.emailPlaceholder || "E.g. student@gmail.com"}
                required
                error={errors.email}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.mobileNumber || "Mobile No."}
                name="mobile_number"
                type="text"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder={
                  t?.mobileNumberPlaceholder ||
                  "Indian mobile: starts 6-9, 10 digits."
                }
                maxLength={10}
                required
                error={errors.mobile_number}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.gender || "Gender"}
                name="gender"
                value={formData.gender}
                onChange={(value) => handleDropdownChange("gender", value)}
                options={genderOptions}
                placeholder={t?.selectGender || "Select Gender"}
                required
                error={errors.gender}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.religion || "Religion"}
                name="religion"
                value={formData.religion}
                onChange={(value) => handleDropdownChange("religion", value)}
                options={religionOptions}
                placeholder={t?.selectReligion || "Select Religion"}
                required
                error={errors.religion}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.caste || "Caste"}
                name="caste"
                type="text"
                value={formData.caste}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("caste")}
                placeholder={t?.castePlaceholder || "E.g. Caste"}
                required
                error={errors.caste}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.category || "Category"}
                name="community"
                value={formData.community}
                onChange={(value) => handleDropdownChange("community", value)}
                options={categoryOptions}
                placeholder={t?.selectCategory || "Select Category"}
                required
                error={errors.community}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.nationality || "Nationality"}
                name="nationality"
                type="text"
                value={formData.nationality}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("nationality")}
                placeholder={t?.nationalityPlaceholder || "E.g. Indian"}
                required
                error={errors.nationality}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.state || "State"}
                name="student_native_state"
                value={formData.student_native_state}
                onChange={(value) =>
                  handleDropdownChange("student_native_state", value)
                }
                options={stateOptions}
                placeholder={t?.selectState || "Select State"}
                required
                error={errors.student_native_state}
              />
            </div>
          </div>

          {/* SECTION: RESIDENTIAL DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.residentialDetails || "Residential Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.areYouResidingWith ||
                  "Are you residing with parents or with a guardian?"
                }
                name="living_with"
                value={formData.living_with}
                onChange={(value) => handleDropdownChange("living_with", value)}
                options={livingWithOptions}
                placeholder={t?.select || "Select"}
                required
                error={errors.living_with}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.fullname || "Name"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_name"
                    : "parent_name"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_name
                    : formData.parent_name
                }
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_name"
                      : "parent_name",
                  )
                }
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_name
                    : errors.parent_name
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.occupation || "Occupation"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_occupation"
                    : "parent_occupation"
                }
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_occupation
                    : formData.parent_occupation
                }
                onChange={(value) =>
                  handleDropdownChange(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_occupation"
                      : "parent_occupation",
                    value,
                  )
                }
                options={occupationOptions}
                placeholder={t?.selectOccupation || "Select Occupation"}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_occupation
                    : errors.parent_occupation
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.annualIncome || "Annual Income"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_annual_salary"
                    : "parent_annual_salary"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_annual_salary
                    : formData.parent_annual_salary
                }
                onChange={handleInputChange}
                placeholder={t?.annualIncomePlaceholder || "E.g. 500000"}
                maxLength={7}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_annual_salary
                    : errors.parent_annual_salary
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.address || "Address"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_address"
                    : "parent_address"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_address
                    : formData.parent_address
                }
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_address"
                      : "parent_address",
                  )
                }
                placeholder={t?.addressPlaceholder || "E.g. Address"}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_address
                    : errors.parent_address
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.city || "City"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_city"
                    : "parent_city"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_city
                    : formData.parent_city
                }
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_city"
                      : "parent_city",
                  )
                }
                placeholder={t?.cityPlaceholder || "E.g. City"}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_city
                    : errors.parent_city
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.district || "District"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_district"
                    : "parent_district"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_district
                    : formData.parent_district
                }
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_district"
                      : "parent_district",
                  )
                }
                placeholder={t?.districtPlaceholder || "E.g. District"}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_district
                    : errors.parent_district
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.state || "State"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_state"
                    : "parent_state"
                }
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_state
                    : formData.parent_state
                }
                onChange={(value) =>
                  handleDropdownChange(
                    formData.living_with?.value === "guardian" ||
                      formData.living_with === "guardian"
                      ? "guardian_state"
                      : "parent_state",
                    value,
                  )
                }
                options={stateOptions}
                placeholder={t?.selectState || "Select State"}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_state
                    : errors.parent_state
                }
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.pincode || "Pincode"}
                name={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? "guardian_pincode"
                    : "parent_pincode"
                }
                type="text"
                value={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? formData.guardian_pincode
                    : formData.parent_pincode
                }
                onChange={handleInputChange}
                placeholder={t?.pincodePlaceholder || "6-digit pincode"}
                maxLength={6}
                required
                error={
                  formData.living_with?.value === "guardian" ||
                  formData.living_with === "guardian"
                    ? errors.guardian_pincode
                    : errors.parent_pincode
                }
              />
            </div>
          </div>

          {/* SECTION: LAST STUDIED DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.lastStudiedDetails || "Last Studied Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.lastStudiedClass || "Last Studied Class"}
                name="last_studied_class"
                value={formData.last_studied_class}
                onChange={(value) =>
                  handleDropdownChange("last_studied_class", value)
                }
                options={classOptions}
                placeholder={t?.selectClass || "Select"}
                required
                error={errors.last_studied_class}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.lastStudiedSchool || "Last Studied School"}
                name="last_studied_school"
                type="text"
                value={formData.last_studied_school}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("last_studied_school")}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.last_studied_school}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.emisNo || "EMIS No"}
                name="emis_id"
                type="text"
                value={formData.emis_id}
                onChange={handleInputChange}
                placeholder={t?.emisNoPlaceholder || "E.g. 1212121212"}
                maxLength={10}
                required
                error={errors.emis_id}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={t?.lastStudiedMedium || "Last Studied Medium"}
                name="last_studied_medium"
                value={formData.last_studied_medium}
                onChange={(value) =>
                  handleRadioChange("last_studied_medium", value)
                }
                options={MEDIUM_OPTIONS}
                required
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.eligibleForHigherStudies || "Eligible for Higher Studies"
                }
                name="qualified_higher_secondary"
                value={formData.qualified_higher_secondary}
                onChange={(value) =>
                  handleRadioChange("qualified_higher_secondary", value)
                }
                options={YES_NO_OPTIONS}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.attachedDocumentType ||
                  "Attached Document (Transfer Certificate / Record Sheet)"
                }
                name="attached_document_type"
                value={formData.attached_document_type}
                onChange={(value) =>
                  handleRadioChange("attached_document_type", value)
                }
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={t?.document || "Document"}
                name="document_path"
                value={formData.document_original_name || null}
                onChange={handleDocumentChange}
                accept=".pdf,.jpg,.jpeg,.png"
                placeholder={
                  formData.document_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
                }
                buttonText={t?.chooseFile || "Choose File"}
                required
                error={errors.document_path}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG."}
              </small>
            </div>
          </div>

          {/* SECTION: PREFERRED COURSE DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.preferredCourseDetails || "Preferred Course Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.preferredClassForAdmission ||
                  "Preferred Class for Admission"
                }
                name="preferred_class"
                value={formData.preferred_class}
                onChange={(value) =>
                  handleDropdownChange("preferred_class", value)
                }
                options={classOptions}
                placeholder={
                  t?.selectPreferredClass || "Select Preferred Class"
                }
                required
                error={errors.preferred_class}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.motherTongue || "Mother Tongue"}
                name="mother_tongue"
                value={formData.mother_tongue}
                onChange={(value) =>
                  handleDropdownChange("mother_tongue", value)
                }
                options={motherTongueOptions}
                placeholder={t?.selectMotherTongue || "Select Mother Tongue"}
                required
                error={errors.mother_tongue}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.preferredFirstLanguageToStudy ||
                  "Preferred First Language to Study"
                }
                name="preferred_first_language"
                value={formData.preferred_first_language}
                onChange={(value) =>
                  handleDropdownChange("preferred_first_language", value)
                }
                options={languageOptions}
                placeholder={t?.select || "Select"}
                required
                error={errors.preferred_first_language}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={t?.preferredMediumToStudy || "Preferred Medium to Study"}
                name="preferred_stream"
                value={formData.preferred_stream}
                onChange={(value) =>
                  handleRadioChange("preferred_stream", value)
                }
                options={MEDIUM_OPTIONS}
                required
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.smallpoxVaccinationTaken || "Smallpox Vaccination Taken?"
                }
                name="is_vaccinated_pox"
                value={formData.is_vaccinated_pox}
                onChange={(value) =>
                  handleRadioChange("is_vaccinated_pox", value)
                }
                options={YES_NO_OPTIONS}
              />
            </div>
          </div>

          {/* SECTION: PREVIOUS SCHOOL ADDRESS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.previousSchoolAddress || "Previous School Address"}
            </h1>
          </div>

          {[1, 2, 3].map((num) => (
            <div className="row" key={num}>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={`${t?.schoolName || "School Name"} ${num}`}
                  name={`previous_school_address_${num}`}
                  type="text"
                  value={formData[`previous_school_address_${num}`]}
                  onChange={handleInputChange}
                  onFocus={() =>
                    handleTamilInputFocus(`previous_school_address_${num}`)
                  }
                  placeholder={t?.enterHere || "Enter here.."}
                  required={num === 1}
                  error={errors[`previous_school_address_${num}`]}
                />
              </div>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={t?.classes || "Classes"}
                  name={`previous_studied_class_${num}`}
                  value={formData[`previous_studied_class_${num}`]}
                  onChange={(value) =>
                    handleDropdownChange(`previous_studied_class_${num}`, value)
                  }
                  options={classOptions}
                  placeholder={t?.selectClass || "Select Class"}
                  required={num === 1}
                  error={errors[`previous_studied_class_${num}`]}
                />
              </div>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={t?.yearOfStudy || "Year of Study"}
                  name={`previous_studied_year_${num}`}
                  value={formData[`previous_studied_year_${num}`]}
                  onChange={(value) =>
                    handleDropdownChange(`previous_studied_year_${num}`, value)
                  }
                  options={YEAR_OPTIONS}
                  placeholder={t?.selectYear || "Select Year"}
                  required={num === 1}
                  error={errors[`previous_studied_year_${num}`]}
                />
              </div>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12">
                <CustomRadioGroup
                  label={t?.mediumOfInstruction || "Medium of Instruction"}
                  name={`previous_studied_medium_${num}`}
                  value={formData[`previous_studied_medium_${num}`]}
                  onChange={(value) =>
                    handleRadioChange(`previous_studied_medium_${num}`, value)
                  }
                  options={MEDIUM_OPTIONS}
                />
              </div>
            </div>
          ))}

          {/* SECTION: IDENTIFICATION MARKS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.identificationMarksOnBody ||
                "Identification Marks on the Body"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.identificationMark1 || "Identification Mark 1"}
                name="identification_mark_1"
                type="text"
                value={formData.identification_mark_1}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("identification_mark_1")}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.identification_mark_1}
              />
            </div>
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.identificationMark2 || "Identification Mark 2"}
                name="identification_mark_2"
                type="text"
                value={formData.identification_mark_2}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("identification_mark_2")}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.identification_mark_2}
              />
            </div>
          </div>

          {/* SECTION: OTHER DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.otherDetails || "Other Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.place || "Place"}
                name="place"
                type="text"
                value={formData.place}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("place")}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.place}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={t?.studentImage || "Student Image"}
                name="student_image"
                value={formData.student_image_original_name || null}
                onChange={handleStudentImageChange}
                accept="image/*"
                placeholder={
                  formData.student_image_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
                }
                buttonText={t?.chooseFile || "Choose File"}
                required
                error={errors.student_image}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.allowedJpgPng || "Allowed: JPG/PNG images."}
              </small>
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              loading={submitting}
              disabled={viewId && admissionStatus !== "Submitted"}
            >
              {isEditMode
                ? t?.verifyAndUpdate || "Verify & Update"
                : t?.verifyApplication || "Verify Application"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdmissionApplicationFormX;
